import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaEdit,
  FaTrash,
  FaCheck,
  FaTimes,
  FaCalendarAlt,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
} from "react-icons/fa";
import { storytellerReservationsAPI } from "../services/api";

const StorytellerReservationDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [reservation, setReservation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});

  // Fetch reservation details
  const fetchReservation = useCallback(async () => {
    try {
      setLoading(true);
      const response = await storytellerReservationsAPI.getReservationById(id);

      if (response.success) {
        setReservation(response.data);
        setFormData(response.data);
      } else {
        setError(response.message || "Failed to fetch reservation");
      }
    } catch (err) {
      setError("An error occurred while fetching reservation");
      console.error("Error fetching reservation:", err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchReservation();
  }, [id, fetchReservation]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle status update
  const handleStatusUpdate = async (newStatus) => {
    try {
      const response = await storytellerReservationsAPI.updateReservationStatus(
        id,
        { status: newStatus }
      );

      if (response.success) {
        setReservation((prev) => ({ ...prev, reservation_status: newStatus }));
        setIsEditing(false);
      } else {
        alert(response.message || "Failed to update status");
      }
    } catch (err) {
      alert("An error occurred while updating status");
      console.error("Error updating status:", err);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await storytellerReservationsAPI.updateReservation(
        id,
        formData
      );

      if (response.success) {
        setReservation(response.data);
        setIsEditing(false);
      } else {
        alert(response.message || "Failed to update reservation");
      }
    } catch (err) {
      alert("An error occurred while updating reservation");
      console.error("Error updating reservation:", err);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this reservation?")) {
      return;
    }

    try {
      const response = await storytellerReservationsAPI.deleteReservation(id);

      if (response.success) {
        navigate("/storyteller-reservations");
      } else {
        alert(response.message || "Failed to delete reservation");
      }
    } catch (err) {
      alert("An error occurred while deleting reservation");
      console.error("Error deleting reservation:", err);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Format currency
  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  // Get status badge class
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "confirmed":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      case "completed":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <div className="mt-2 text-sm text-red-700">
              <p>{error}</p>
            </div>
            <div className="mt-4">
              <button
                onClick={fetchReservation}
                className="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!reservation) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900">
          Reservation not found
        </h3>
        <p className="text-gray-500">
          The reservation you're looking for doesn't exist.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate("/storyteller-reservations")}
              className="text-gray-400 hover:text-gray-600"
            >
              <FaArrowLeft className="h-6 w-6" />
            </button>
            <div>
              <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                Reservation Details
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                {reservation.reservation_id}
              </p>
            </div>
          </div>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <FaEdit className="h-4 w-4 mr-2" />
            {isEditing ? "Cancel Edit" : "Edit"}
          </button>
          <button
            onClick={handleDelete}
            className="ml-3 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            <FaTrash className="h-4 w-4 mr-2" />
            Delete
          </button>
        </div>
      </div>

      {/* Status Badge */}
      <div className="flex items-center space-x-4">
        <span
          className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusBadgeClass(
            reservation.reservation_status
          )}`}
        >
          {reservation.reservation_status}
        </span>
        <div className="flex space-x-2">
          <button
            onClick={() => handleStatusUpdate("confirmed")}
            className={`px-3 py-1 text-xs rounded-full ${
              reservation.reservation_status === "confirmed"
                ? "bg-green-100 text-green-800"
                : "bg-gray-100 text-gray-600 hover:bg-green-100 hover:text-green-800"
            }`}
          >
            <FaCheck className="h-3 w-3 mr-1 inline" />
            Confirm
          </button>
          <button
            onClick={() => handleStatusUpdate("cancelled")}
            className={`px-3 py-1 text-xs rounded-full ${
              reservation.reservation_status === "cancelled"
                ? "bg-red-100 text-red-800"
                : "bg-gray-100 text-gray-600 hover:bg-red-100 hover:text-red-800"
            }`}
          >
            <FaTimes className="h-3 w-3 mr-1 inline" />
            Cancel
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Customer Information */}
        <div className="lg:col-span-2">
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                Customer Information
              </h3>

              {isEditing ? (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        First Name
                      </label>
                      <input
                        type="text"
                        name="reservation_first_name"
                        value={formData.reservation_first_name || ""}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Last Name
                      </label>
                      <input
                        type="text"
                        name="reservation_last_name"
                        value={formData.reservation_last_name || ""}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        name="reservation_email"
                        value={formData.reservation_email || ""}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone
                      </label>
                      <input
                        type="tel"
                        name="reservation_phone"
                        value={formData.reservation_phone || ""}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setIsEditing(false)}
                      className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                    >
                      Save Changes
                    </button>
                  </div>
                </form>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0 h-12 w-12">
                      <div className="h-12 w-12 rounded-full bg-gray-300 flex items-center justify-center">
                        <FaUser className="h-6 w-6 text-gray-600" />
                      </div>
                    </div>
                    <div>
                      <h4 className="text-lg font-medium text-gray-900">
                        {reservation.reservation_first_name}{" "}
                        {reservation.reservation_last_name}
                      </h4>
                      <div className="flex items-center text-sm text-gray-500">
                        <FaEnvelope className="h-4 w-4 mr-2" />
                        {reservation.reservation_email}
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        <FaPhone className="h-4 w-4 mr-2" />
                        {reservation.reservation_phone}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Reservation Details */}
          <div className="mt-6 bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                Reservation Details
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">
                    Date & Time
                  </h4>
                  <div className="flex items-center text-sm text-gray-900">
                    <FaCalendarAlt className="h-4 w-4 mr-2" />
                    {formatDate(reservation.reservation_date)}
                  </div>
                  {reservation.reservation_time && (
                    <div className="text-sm text-gray-500 ml-6">
                      {reservation.reservation_time}
                    </div>
                  )}
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">
                    Group Size
                  </h4>
                  <div className="text-sm text-gray-900">
                    {reservation.people} people
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">
                    Amount
                  </h4>
                  <div className="text-sm text-gray-900">
                    {formatCurrency(reservation.amount, reservation.currency)}
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">
                    Created
                  </h4>
                  <div className="text-sm text-gray-900">
                    {formatDate(reservation.createdAt)}
                  </div>
                </div>
              </div>

              {reservation.reservation_message && (
                <div className="mt-6">
                  <h4 className="text-sm font-medium text-gray-500 mb-2">
                    Message
                  </h4>
                  <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                    {reservation.reservation_message}
                  </div>
                </div>
              )}

              {reservation.special_requests && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-500 mb-2">
                    Special Requests
                  </h4>
                  <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
                    {reservation.special_requests}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Storyteller Information */}
        <div className="lg:col-span-1">
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                Storyteller Information
              </h3>

              {reservation.storyteller ? (
                <div className="space-y-4">
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0 h-12 w-12">
                      <img
                        className="h-12 w-12 rounded-full object-cover"
                        src={
                          reservation.storyteller.image
                            ? `https://songstarktours.s3.ap-northeast-2.amazonaws.com/${reservation.storyteller.image}`
                            : "/default-avatar.png"
                        }
                        alt={reservation.storyteller.name}
                        onError={(e) => {
                          e.target.src = "/default-avatar.png";
                        }}
                      />
                    </div>
                    <div>
                      <h4 className="text-lg font-medium text-gray-900">
                        {reservation.storyteller.name}
                      </h4>
                      <div className="flex items-center text-sm text-gray-500">
                        <FaMapMarkerAlt className="h-4 w-4 mr-2" />
                        {reservation.storyteller.location}
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-2">
                      Specialty
                    </h4>
                    <div className="text-sm text-gray-900">
                      {reservation.storyteller.specialty}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-2">
                      Price per Person
                    </h4>
                    <div className="text-sm text-gray-900">
                      {formatCurrency(reservation.storyteller.pricePerPerson)}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-gray-500">
                  Storyteller information not available
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StorytellerReservationDetails;
